import { useEffect, useRef } from 'react';

export interface UseJitteredPollOptions {
  /**
   * Base delay between polls. Actual delay is `intervalMs + [0, jitterMs)`
   * per cycle to avoid synchronized bursts across many clients.
   */
  readonly intervalMs: number;
  /**
   * Maximum extra random delay added per cycle. Set to `0` to disable
   * jitter (useful in tests). Never negative.
   */
  readonly jitterMs: number;
  /**
   * When false, the poll loop does not run. Flipping true → false
   * cancels the pending tick and stops scheduling. Flipping false →
   * true starts a fresh cycle. Callers use this to gate on audit id
   * presence, feature flags, etc.
   */
  readonly enabled: boolean;
  /**
   * Fires each tick. Return a promise (or void) — the next tick is
   * scheduled only after this settles, so overlapping polls never
   * fire even if the work takes longer than `intervalMs`. Read
   * through a ref internally so callers don't need to memoize.
   */
  readonly onPoll: () => Promise<void> | void;
  /**
   * Called at each tick BEFORE `onPoll`. Return true to SKIP this
   * tick (keeping the loop alive for the next tick). Useful for
   * caller-owned gates like "a mutation is in flight" or "we're on
   * a route where poll isn't wanted right now". Read through a ref
   * so gating on evolving state doesn't restart the timer.
   *
   * Note: this hook also skips ticks when `document.hidden` is true,
   * so callers don't need to plumb visibility into `isPaused` — the
   * hidden check is applied on top of the caller's predicate.
   */
  readonly isPaused?: () => boolean;
  /**
   * Give up after this many consecutive errors from `onPoll`. Any
   * successful poll resets the count. Defaults to 5 — matches V1's
   * `useQuestionResponsePoller` cadence for consistency. A value of
   * 0 disables the backoff (poll forever regardless of errors).
   */
  readonly maxConsecutiveErrors?: number;
}

const DEFAULT_MAX_CONSECUTIVE_ERRORS = 5;

/**
 * Jittered background polling loop. Fires `onPoll` on a randomized
 * cadence (`intervalMs + [0, jitterMs)` per tick), pausing when the
 * tab is hidden or when the caller-provided `isPaused` returns true.
 * Errors are counted; the loop stops silently after
 * `maxConsecutiveErrors` consecutive failures so a broken backend
 * doesn't get hammered.
 *
 * Design constraints this hook honors:
 *
 * 1. **No overlapping polls.** The next tick is scheduled AFTER the
 *    previous `onPoll` settles. If `onPoll` takes 30s and the
 *    interval is 15s, ticks fire ~30s apart, not two-at-a-time.
 *
 * 2. **Reference-stable callbacks.** `onPoll` and `isPaused` are read
 *    through refs, so parents can pass a fresh function each render
 *    without restarting the timer. Only `enabled`, `intervalMs`,
 *    `jitterMs`, and `maxConsecutiveErrors` reset the loop when they
 *    change — those are the true structural parameters.
 *
 * 3. **StrictMode-safe.** The effect returns a cleanup that flips a
 *    `stopped` flag and clears the pending timer. A double-invoke
 *    tears down the first mount cleanly before the second mount
 *    starts, so no stray ticks survive the remount.
 *
 * 4. **Post-settle intent re-check.** Every branch that schedules the
 *    next tick first checks the `stopped` flag. If a poll's promise
 *    resolves 5s after the component unmounted, the response is
 *    dropped on the floor rather than kicking off a doomed setTimeout.
 *
 * @example
 *   useJitteredPoll({
 *     intervalMs: 15_000,
 *     jitterMs: 3_000,
 *     enabled: !!auditId,
 *     onPoll: () => queryClient.refetchQueries({ queryKey: ['audit-summary', auditId] }),
 *     isPaused: () => isMutatingRef.current,
 *   });
 */
export function useJitteredPoll(options: UseJitteredPollOptions): void {
  const {
    intervalMs,
    jitterMs,
    enabled,
    onPoll,
    isPaused,
    maxConsecutiveErrors = DEFAULT_MAX_CONSECUTIVE_ERRORS,
  } = options;

  // Latest callback slots — refreshed on every render so the timer
  // callback always calls the newest `onPoll`/`isPaused` without a
  // timer restart. See design constraint #2 in the docblock.
  const onPollRef = useRef(onPoll);
  const isPausedRef = useRef(isPaused);
  useEffect(() => {
    onPollRef.current = onPoll;
  }, [onPoll]);
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    if (!enabled) return;
    const loop = startPollLoop({
      intervalMs,
      jitterMs,
      maxConsecutiveErrors,
      onPollRef,
      isPausedRef,
    });
    return loop.stop;
    // `onPoll` and `isPaused` are intentionally NOT deps — see
    // docblock design constraint #2. Only the structural params
    // reset the loop.
  }, [enabled, intervalMs, jitterMs, maxConsecutiveErrors]);
}

// ─── internals ──────────────────────────────────────────────────────────────

interface PollLoopHandle {
  readonly stop: () => void;
}

interface PollLoopConfig {
  readonly intervalMs: number;
  readonly jitterMs: number;
  readonly maxConsecutiveErrors: number;
  readonly onPollRef: React.MutableRefObject<UseJitteredPollOptions['onPoll']>;
  readonly isPausedRef: React.MutableRefObject<UseJitteredPollOptions['isPaused']>;
}

/**
 * Encapsulate the mutable per-mount state (stop flag, timer handle,
 * error count) inside a small helper so the hook's `useEffect` body
 * stays a two-liner. Keeping the loop as an independent function
 * also lets it be reasoned about (and tested indirectly through the
 * hook) without wading through hook-lifecycle noise.
 */
function startPollLoop(config: PollLoopConfig): PollLoopHandle {
  const { intervalMs, jitterMs, maxConsecutiveErrors, onPollRef, isPausedRef } = config;

  let stopped = false;
  let timerId: number | null = null;
  let consecutiveErrors = 0;

  const shouldSkipTick = (): boolean =>
    document.hidden || (isPausedRef.current?.() ?? false);

  const shouldStopOnErrorThreshold = (): boolean =>
    maxConsecutiveErrors > 0 && consecutiveErrors >= maxConsecutiveErrors;

  const scheduleNext = (): void => {
    if (stopped) return;
    const delay = intervalMs + Math.random() * jitterMs;
    // `window.setTimeout` (not bare `setTimeout`) so TypeScript picks
    // the numeric-handle DOM overload rather than Node's Timeout.
    // Matches the pattern in `useAutosaveStatus`.
    timerId = window.setTimeout(() => {
      timerId = null;
      void runOnce();
    }, delay);
  };

  const recordResult = (succeeded: boolean): void => {
    if (succeeded) {
      consecutiveErrors = 0;
      scheduleNext();
      return;
    }
    consecutiveErrors += 1;
    if (shouldStopOnErrorThreshold()) return;
    scheduleNext();
  };

  // Post-settle intent re-check — design constraint #4. A poll
  // that resolves after unmount lands here with `stopped=true`
  // and bails without touching state or scheduling a doomed
  // follow-up tick.
  const onSettled = (succeeded: boolean): void => {
    if (stopped) return;
    recordResult(succeeded);
  };

  const runOnce = (): void => {
    if (stopped) return;
    if (shouldSkipTick()) {
      // Skipped ticks don't count against errors — nothing was
      // attempted, so a long backgrounded tab doesn't accumulate
      // failures that would trip the stop threshold on resume.
      scheduleNext();
      return;
    }
    // Wrap the callback's return in `Promise.resolve` so both sync
    // and async `onPoll` implementations funnel through the same
    // settle handler. `.then(_, _)` (two-arg form) is used instead
    // of `.then().catch()` so a promise that rejects synchronously
    // in the success handler wouldn't accidentally re-trigger the
    // error path. Coding-standards rule 12 bans `await` in hooks/;
    // this is the callback-chain equivalent.
    Promise.resolve(onPollRef.current()).then(
      () => onSettled(true),
      () => onSettled(false),
    );
  };

  scheduleNext();

  return {
    stop: (): void => {
      stopped = true;
      if (timerId !== null) {
        window.clearTimeout(timerId);
        timerId = null;
      }
    },
  };
}
